import React, { useState } from 'react';
import PropTypes from 'prop-types';
import "../style.css";

/**
 * Card component for both beat and noise presets
 *
 * @param {Object} preset - The preset data object
 * @param {boolean} isActive - Whether this preset is currently active
 * @param {function} onActivate - Function to call when activating the preset
 * @param {string} cardType - Type of card ('beat' or 'noise')
 */
const SoundCard = ({
  preset,
  isActive,
  onActivate,
  cardType
}) => {
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const isBeat = cardType === 'beat';
 
  // Close info popup when clicking outside
  React.useEffect(() => {
    if (isInfoVisible) {
      const handleOutsideClick = (e) => {
        if (!e.target.closest('.info-container')) {
          setIsInfoVisible(false);
        }
      };
      
      document.addEventListener('click', handleOutsideClick);
      return () => document.removeEventListener('click', handleOutsideClick);
    }
  }, [isInfoVisible]);
  
  const handleInfoClick = (e) => {
    e.stopPropagation();
    setIsInfoVisible(!isInfoVisible);
  };
 
  const handlePlayClick = (e) => {
    e.stopPropagation();
    onActivate(preset);
  };
 
  return (
    <div
      role="listitem"
      className={`${isBeat ? 'beat-card' : 'noise-card'} ${isActive ? 'active' : ''} ${preset.color}`}
      onClick={() => onActivate(preset)}
    >
      <div className={isBeat ? preset.color : 'noise-content'}>
        <div className={isBeat ? `beat-header ${preset.color}` : 'noise-header'}>
          <h4 className={isBeat ? 'beat-title' : 'noise-title'}>{preset.name}</h4>
          {isBeat && <span className="frequency-value">{preset.frequency} Hz</span>}
          <div className="info-container">
            <button
              onClick={handleInfoClick}
              className={`header-info-button ${preset.color}`}
              aria-label={isInfoVisible ? "Hide information" : "Show information"}
              aria-expanded={isInfoVisible}
            >
              <span className="info-icon">{isInfoVisible ? '✕' : 'ⓘ'}</span>
            </button>
           
            {isInfoVisible && (
              <div className="beat-description-popup" onClick={(e) => e.stopPropagation()}>
                <p>{preset.description}</p>
              </div>
            )}
          </div>
        </div>
       
        <div className={isBeat ? 'beat-play-container' : ''}>
          <button
            onClick={handlePlayClick}
            className={isBeat
              ? `play-button-icon ${preset.color} ${isActive ? 'active' : ''}`
              : `play-button ${isActive ? 'playing' : ''}`
            }
            aria-label={isActive ? `Stop ${preset.name}` : `Play ${preset.name}`}
            aria-pressed={isActive}
          >
            {isActive ? (
              <svg viewBox="0 0 24 24" width="20" height="20" className="stop-icon">
                <rect x="6" y="6" width="12" height="12" fill="currentColor" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20" className="play-icon">
                <path d="M8 5v14l11-7z" fill="currentColor" />
              </svg>
            )}
          </button>
        </div>
       
        {isBeat && (
          <div className={`beat-label ${preset.color}`}>
            <p>{preset.label}</p>
          </div>
        )}
      </div>
    </div>
  );
};

SoundCard.propTypes = {
  preset: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    frequency: PropTypes.number,
    label: PropTypes.string,
    color: PropTypes.string
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  onActivate: PropTypes.func.isRequired,
  cardType: PropTypes.oneOf(['beat', 'noise']).isRequired
};

export default SoundCard;